/**
 * Proactive greeting bubble.
 *
 * Sits next to the launcher and says the one thing the launcher cannot: why
 * you might want to talk. It appears uninvited, so two things matter more than
 * looks — dismissing it must be at least as easy as accepting it, and the
 * dismiss target must be big enough to hit on the first try. A greeting you
 * cannot get rid of is an ad.
 *
 * Timing lives in `NudgeController`; this element only renders and reports.
 */

import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";

import { closeIcon } from "./icons.js";

@customElement("deepagent-nudge")
export class DeepAgentNudge extends LitElement {
  static override styles = css`
    :host {
      display: block;
      max-width: 280px;
    }

    .bubble {
      position: relative;
      display: block;
      background: var(--da-bg);
      border: 1px solid var(--da-border);
      border-radius: var(--da-radius-lg, 2px);
      box-shadow: var(--da-shadow-panel);
      animation: da-nudge-in 260ms cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .message {
      appearance: none;
      border: none;
      background: transparent;
      cursor: pointer;
      display: block;
      width: 100%;
      /* Right padding clears the dismiss button so a long message never runs
         underneath it. */
      padding: 13px 40px 13px 14px;
      font-family: inherit;
      font-size: 14px;
      line-height: 1.45;
      font-weight: 500;
      color: var(--da-text);
      text-align: left;
      border-radius: inherit;
      transition: background-color 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    @media (hover: hover) {
      .message:hover {
        background: var(--da-surface-2);
      }
    }

    .message:focus-visible,
    .dismiss:focus-visible {
      outline: 2px solid var(--da-accent);
      outline-offset: 2px;
    }

    .dismiss {
      position: absolute;
      top: 6px;
      right: 6px;
      appearance: none;
      border: none;
      background: transparent;
      color: var(--da-text-muted);
      /* 28px is the smallest that still reads as a comfortable tap target here;
         the bubble is small and a larger square would dominate it. */
      width: 28px;
      height: 28px;
      border-radius: var(--da-radius, 2px);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition:
        background-color 180ms cubic-bezier(0.16, 1, 0.3, 1),
        color 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    @media (hover: hover) {
      .dismiss:hover {
        background: var(--da-surface-2);
        color: var(--da-text);
      }
    }

    .dismiss svg {
      width: 14px;
      height: 14px;
    }

    @keyframes da-nudge-in {
      from { transform: translateY(6px); opacity: 0; }
      to   { transform: translateY(0);   opacity: 1; }
    }

    @media (prefers-reduced-motion: reduce) {
      .bubble { animation: none; }
    }
  `;

  @property({ type: String }) message = "";
  @property({ type: String }) dismissLabel = "Close";

  private emit(name: "nudge-click" | "nudge-dismiss") {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true }));
  }

  override render() {
    return html`
      <div class="bubble" aria-live="polite">
        <button
          type="button"
          class="message"
          @click=${() => this.emit("nudge-click")}
        >
          ${this.message}
        </button>
        <button
          type="button"
          class="dismiss"
          aria-label=${this.dismissLabel}
          @click=${() => this.emit("nudge-dismiss")}
        >
          ${closeIcon()}
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "deepagent-nudge": DeepAgentNudge;
  }
}
